import axios from 'axios';

// In-memory cache for GET requests
const memoryCache = new Map();
// In-flight promises to deduplicate simultaneous requests (e.g. React StrictMode)
const inFlightRequests = new Map();

const CACHE_TTL_MS = process.env.NODE_ENV === 'development' ? 5 * 1000 : 5 * 60 * 1000;

const getCacheKey = (config) => {
  return `${(config.method || 'get').toLowerCase()}:${config.url}`;
};

// Request Interceptor: return cached response or deduplicate in-flight requests
axios.interceptors.request.use(
  (config) => {
    if ((config.method || 'get').toLowerCase() !== 'get') {
      return config;
    }

    const key = getCacheKey(config);

    // 1. Check in-memory cache
    const cached = memoryCache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      config.adapter = () =>
        Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK (memory cache)',
          headers: {},
          config,
          request: {}
        });
      return config;
    }

    // 2. Check sessionStorage (bypassed in development so page refresh immediately gets updated data)
    if (process.env.NODE_ENV !== 'development') {
      try {
        const sessionRaw = sessionStorage.getItem(`foton_cache_${key}`);
        if (sessionRaw) {
          const parsed = JSON.parse(sessionRaw);
          if (Date.now() - parsed.timestamp < CACHE_TTL_MS) {
            memoryCache.set(key, parsed);
            config.adapter = () =>
              Promise.resolve({
                data: parsed.data,
                status: 200,
                statusText: 'OK (session cache)',
                headers: {},
                config,
                request: {}
              });
            return config;
          }
        }
      } catch (e) {
        // sessionStorage unavailable
      }
    }

    // 3. Deduplicate simultaneous in-flight requests
    if (inFlightRequests.has(key)) {
      config.adapter = () => inFlightRequests.get(key);
      return config;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: cache successful responses and gracefully handle 429 rate-limiting
axios.interceptors.response.use(
  (response) => {
    const config = response.config;
    if ((config.method || 'get').toLowerCase() === 'get' && response.status === 200) {
      const key = getCacheKey(config);
      inFlightRequests.delete(key);
      const cacheEntry = {
        timestamp: Date.now(),
        data: response.data
      };
      memoryCache.set(key, cacheEntry);
      if (process.env.NODE_ENV !== 'development') {
        try {
          sessionStorage.setItem(`foton_cache_${key}`, JSON.stringify(cacheEntry));
        } catch (e) {
          // quota exceeded
        }
      }
    }
    return response;
  },
  (error) => {
    const config = error.config;
    if (config) {
      const key = getCacheKey(config);
      inFlightRequests.delete(key);

      // Handle 429 Too Many Requests or network disconnects
      if (error.response?.status === 429 || !error.response) {
        console.warn(`[Axios Interceptor] Handled ${error.response?.status || 'Network'} error gracefully for: ${config.url}`);

        // Try stale memory cache
        const stale = memoryCache.get(key);
        if (stale) {
          return Promise.resolve({
            data: stale.data,
            status: 200,
            statusText: 'OK (stale memory cache fallback)',
            headers: {},
            config,
            request: {}
          });
        }

        // Try stale session cache
        try {
          const sessionRaw = sessionStorage.getItem(`foton_cache_${key}`);
          if (sessionRaw) {
            const parsed = JSON.parse(sessionRaw);
            return Promise.resolve({
              data: parsed.data,
              status: 200,
              statusText: 'OK (stale session cache fallback)',
              headers: {},
              config,
              request: {}
            });
          }
        } catch (e) {}

        // Resolve gracefully with safe fallback data instead of throwing unhandled rejection
        return Promise.resolve({
          data: {
            success: false,
            data: null,
            message: 'API rate limit reached or server unavailable. Using component fallback data.'
          },
          status: error.response?.status || 429,
          statusText: 'Rate Limited (Handled)',
          headers: {},
          config,
          request: {}
        });
      }
    }
    return Promise.reject(error);
  }
);

export default axios;
